import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, setDoc, collection, query, where, getDocs, updateDoc, addDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, updateProfile } from 'firebase/auth';
import { db, auth } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { VIDEO_BASE_URL } from '../../config/api';
import { Loader2, Save, Lock, CheckCircle2, AlertCircle, User, Mail, Phone, Palette, StickyNote, Search, ChevronDown, Camera, GraduationCap, BookOpen, Clock, Play, Award, Medal, Eye, Sparkles, Star, Compass, Target, History, Calendar } from 'lucide-react';
import PasswordInput from '../../components/auth/PasswordInput';
import PhoneInput from '../../components/ui/PhoneInput';

const DESIGN_LEVELS = { beginner: 'مبتدئ', intermediate: 'متوسط', advanced: 'متقدم' };

export default function AccountSettings() {
  const { currentUser, userData, refreshUserData, isProfileComplete } = useAuth();

  // Tabs layout — support deep-linking via ?tab= query param
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return ['personal', 'security', 'academic', 'journey'].includes(tabParam) ? tabParam : 'personal';
  });

  // Profile form
  const [form, setForm] = useState({
    nameAr: '', nameEn: '', username: '', phone: '', designLevel: 'beginner', notes: '', countryCode: '+967'
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Avatar Upload State
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Password form
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '', isNewValid: false });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  // Academic Data State
  const [academicData, setAcademicData] = useState([]);
  const [loadingAcademic, setLoadingAcademic] = useState(false);

  // Journey Data State
  const [journeyData, setJourneyData] = useState([]);
  const [loadingJourney, setLoadingJourney] = useState(false);

  // Load existing personal data
  useEffect(() => {
    if (userData) {
      setForm({
        nameAr: userData.nameAr || userData.name || '',
        nameEn: userData.nameEn || '',
        username: userData.username || '',
        phone: userData.phone?.replace(/^\+\d+\s?/, '') || userData.phone || '',
        countryCode: (userData.phone && userData.phone.startsWith('+')) ? (userData.phone.split(' ')[0] || '+967') : '+967',
        designLevel: userData.designLevel || 'beginner',
        notes: userData.notes || '',
      });
      if (userData.photoURL) {
        setAvatarPreview(userData.photoURL);
      }
    }
  }, [userData]);

  // Load academic data when switching to the academic or journey tab
  useEffect(() => {
    if ((activeTab === 'academic' || activeTab === 'journey') && academicData.length === 0) {
      const fetchAcademicData = async () => {
        setLoadingAcademic(true);
        try {
          const enrollSnap = await getDocs(query(collection(db, 'enrollments'), where('userId', '==', currentUser.uid)));
          const enrollments = enrollSnap.docs.map(d => ({ id: d.id, ...d.data() }));

          if (enrollments.length === 0) {
            setAcademicData([]);
            setLoadingAcademic(false);
            return;
          }

          const coursePromises = enrollments.map(async (enr) => {
            const courseDoc = await getDoc(doc(db, 'courses', enr.courseId));
            if (courseDoc.exists()) {
              return { course: { id: courseDoc.id, ...courseDoc.data() }, enrollment: enr };
            }
            return null;
          });

          const results = (await Promise.all(coursePromises)).filter(Boolean);
          setAcademicData(results);
        } catch (err) {
          console.error("Error fetching academic data:", err);
        } finally {
          setLoadingAcademic(false);
        }
      };

      if (currentUser) {
        fetchAcademicData();
      }
    }
  }, [activeTab, currentUser, academicData.length]);

  // Load journey data
  useEffect(() => {
    if (activeTab === 'journey' && journeyData.length === 0) {
      const fetchJourney = async () => {
        setLoadingJourney(true);
        try {
          const q = query(
            collection(db, 'submissions'),
            where('studentId', '==', currentUser.uid),
            where('status', '==', 'graded')
          );
          const snap = await getDocs(q);
          const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          data.sort((a, b) => (b.gradedAt?.toMillis() || 0) - (a.gradedAt?.toMillis() || 0));
          setJourneyData(data);
        } catch (err) {
          console.error("Error fetching journey data:", err);
        } finally {
          setLoadingJourney(false);
        }
      };

      if (currentUser) fetchJourney();
    }
  }, [activeTab, currentUser, journeyData.length]);

  const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB max
        alert('حجم الصورة كبير جداً. الحد الأقصى هو 2 ميغابايت.');
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setAvatarPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!form.nameAr.trim() || !form.nameEn.trim() || !form.username.trim() || !form.phone.trim()) {
      alert('يرجى تعبئة جميع الحقول المطلوبة (*)');
      return;
    }
    setSaving(true);
    setSaveSuccess(false);
    try {
      let finalAvatarUrl = avatarPreview;

      if (avatarFile) {
        setUploadingAvatar(true);
        const reader = new FileReader();
        const base64Promise = new Promise(resolve => {
          reader.onload = ev => resolve(ev.target.result);
          reader.readAsDataURL(avatarFile);
        });
        const base64Data = await base64Promise;

        const uploadRes = await fetch(`${VIDEO_BASE_URL}/api/user/upload-avatar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageBase64: base64Data })
        });
        const uploadData = await uploadRes.json();

        if (uploadData.success) {
          finalAvatarUrl = uploadData.avatarUrl;
          await updateProfile(auth.currentUser, { photoURL: finalAvatarUrl });
        } else {
          throw new Error('Failed to upload image.');
        }
      }

      // Validate Unique Username
      const usernameLower = form.username.trim().toLowerCase();
      const usersQ = query(collection(db, 'users'), where('username', '==', usernameLower));
      const usersSnap = await getDocs(usersQ);
      
      let isUsernameTaken = false;
      usersSnap.forEach(docSnap => {
        if (docSnap.id !== currentUser.uid) {
          isUsernameTaken = true;
        }
      });

      if (isUsernameTaken) {
        alert('اسم المستخدم هذا مستخدم بالفعل من قبل شخص آخر. يرجى اختيار اسم مختلف.');
        setSaving(false);
        return;
      }

      await setDoc(doc(db, 'users', currentUser.uid), {
        nameAr: form.nameAr.trim(),
        nameEn: form.nameEn.trim(),
        username: usernameLower,
        name: form.nameAr.trim(),
        phone: `${form.countryCode} ${form.phone.trim()}`,
        designLevel: form.designLevel,
        notes: form.notes.trim(),
        photoURL: finalAvatarUrl || null,
      }, { merge: true });
      await refreshUserData();

      if (avatarFile) {
        setAvatarPreview(finalAvatarUrl);
        setAvatarFile(null);
      }

      try {
        const fullPhone = `${form.countryCode} ${form.phone.trim()}`;
        const customersRef = collection(db, 'customers');
        const existing = await getDocs(query(customersRef, where('userId', '==', currentUser.uid)));
        const customerData = {
          name: form.nameAr.trim(),
          nameEn: form.nameEn.trim(),
          username: usernameLower,
          whatsapp: fullPhone,
          email: currentUser.email || '',
          source: 'signup',
          userId: currentUser.uid,
        };
        if (!existing.empty) {
          await updateDoc(doc(db, 'customers', existing.docs[0].id), customerData);
        } else {
          const byPhone = await getDocs(query(customersRef, where('whatsapp', '==', fullPhone)));
          if (!byPhone.empty) {
            await updateDoc(doc(db, 'customers', byPhone.docs[0].id), { ...customerData });
          } else {
            await addDoc(customersRef, { ...customerData, level: 'beginner', status: 'interested', amountPaid: 0, createdAt: serverTimestamp() });
          }
        }
      } catch (custErr) {
        console.warn('Could not upsert customer:', custErr);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error(err);
      alert('حدث خطأ أثناء الحفظ.');
    } finally {
      setSaving(false);
      setUploadingAvatar(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess(false);

    if (pwForm.newPw.length < 6) {
      setPwError('كلمة السر الجديدة يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    if (pwForm.newPw !== pwForm.confirm) {
      setPwError('كلمة السر الجديدة وتأكيدها غير متطابقتين');
      return;
    }

    setPwSaving(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, pwForm.current);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, pwForm.newPw);
      setPwSuccess(true);
      setPwForm({ current: '', newPw: '', confirm: '', isNewValid: false });
      setTimeout(() => setPwSuccess(false), 3000);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPwError('كلمة السر الحالية غير صحيحة');
      } else {
        setPwError('حدث خطأ أثناء تغيير كلمة السر');
      }
    } finally {
      setPwSaving(false);
    }
  };

  const renderPersonalDataTab = () => (
    <form onSubmit={handleSaveProfile} className="bg-white/70 dark:bg-zinc-900/50 backdrop-blur-2xl border border-slate-200/50 dark:border-white/5 rounded-2xl p-8 shadow-xl shadow-slate-200/50 dark:shadow-black/50 space-y-8 animate-in fade-in duration-500">
      <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3 mb-2">
        <div className="p-3 bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400 rounded-xl">
          <User className="w-6 h-6" />
        </div>
        إدارة البيانات الشخصية
      </h2>

      <div className="flex flex-col sm:flex-row items-center gap-6 pb-6 border-b border-slate-200 dark:border-white/10">
        <div className="relative group">
          <div className="w-24 h-24 rounded-full border-4 border-white dark:border-zinc-800 shadow-xl overflow-hidden bg-slate-100 dark:bg-zinc-800 flex items-center justify-center relative">
            {avatarPreview ? (
              <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
            ) : currentUser?.photoURL ? (
              <img src={currentUser.photoURL} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <span className="text-3xl font-black text-slate-400 dark:text-zinc-500">
                {form.nameAr ? form.nameAr.charAt(0) : ''}
              </span>
            )}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
              <Camera className="w-8 h-8 text-white" />
            </div>
          </div>

          <input
            type="file"
            accept="image/*"
            onChange={handleAvatarSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 rounded-full"
          />
          {uploadingAvatar && (
            <div className="absolute inset-0 bg-white/80 dark:bg-black/80 rounded-full flex items-center justify-center z-20">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          )}

          <div className="absolute -bottom-2 -right-2 bg-amber-400 text-amber-900 p-1.5 rounded-full shadow-lg z-10 pointer-events-none ring-4 ring-white dark:ring-zinc-900 border border-amber-300">
            <Camera className="w-4 h-4" />
          </div>
        </div>
        <div className="text-center sm:text-right">
          <h3 className="font-bold text-slate-900 dark:text-white text-lg">الصورة الشخصية</h3>
          <p className="text-slate-500 dark:text-zinc-400 text-md mt-1">يُفضل رفع صورة مربعة، واضحة وصغيرة الحجم بحجم أقصاه 2MB.</p>
          {avatarFile && <span className="text-md font-bold text-primary-500 mt-2 block">سيتم تحديث الصورة عند الضغط على "حفظ البيانات"</span>}
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">
            الاسم الرباعي (عربي) <span className="text-rose-500">*</span>
          </label>
          <input
            type="text" required
            value={form.nameAr}
            onChange={e => setForm({ ...form, nameAr: e.target.value })}
            placeholder="محمد أحمد علي العمري"
            className="w-full px-5 py-4 bg-white/50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 font-bold text-slate-900 dark:text-white transition-all shadow-sm hover:bg-white dark:hover:bg-white/5"
          />
        </div>

        <div>
          <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">
            الاسم الرباعي (إنجليزي) <span className="text-rose-500">*</span>
          </label>
          <input
            type="text" required dir="ltr"
            value={form.nameEn}
            onChange={e => setForm({ ...form, nameEn: e.target.value })}
            placeholder="Mohammed Ahmed Ali Al-Amri"
            className="w-full px-5 py-4 bg-white/50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 font-bold text-slate-900 dark:text-white transition-all shadow-sm hover:bg-white dark:hover:bg-white/5"
            style={{ textAlign: 'left' }}
          />
        </div>

        <div>
          <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-2">
            <span className="text-slate-400 font-sans font-bold">@</span> اسم المستخدم <span className="text-rose-500">*</span>
          </label>
          <input
            type="text" required dir="ltr"
            value={form.username}
            onChange={e => setForm({ ...form, username: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
            placeholder="student_24"
            className="w-full px-5 py-4 bg-white/50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 font-bold text-slate-900 dark:text-white transition-all shadow-sm hover:bg-white dark:hover:bg-white/5"
            style={{ textAlign: 'left' }}
          />
        </div>

        <div>
          <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-400" /> البريد الإلكتروني
          </label>
          <input
            type="email" readOnly
            value={currentUser?.email || ''}
            className="w-full px-5 py-4 bg-slate-100/50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl text-slate-500 dark:text-zinc-500 font-mono cursor-not-allowed shadow-inner"
            dir="ltr"
          />
        </div>

        <PhoneInput
          label="رقم الواتساب"
          phoneValue={form.phone}
          onPhoneChange={e => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, '') })}
          countryCodeValue={form.countryCode}
          onCountryCodeChange={code => setForm({ ...form, countryCode: code })}
          required={true}
        />

        <div>
          <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-2">
            <Palette className="w-4 h-4 text-slate-400" /> مستواك في التصميم
          </label>
          <div className="relative">
            <select
              value={form.designLevel}
              onChange={e => setForm({ ...form, designLevel: e.target.value })}
              className="w-full px-5 py-4 bg-white/50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 font-bold text-slate-900 dark:text-white appearance-none cursor-pointer hover:bg-white dark:hover:bg-white/5 transition-all shadow-sm"
            >
              {Object.entries(DESIGN_LEVELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <ChevronDown className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-slate-400" /> مساحتك الخاصة (اختياري)
          </label>
          <textarea
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            rows={4}
            placeholder="نطمح لمعرفة طموحاتك ومجال تركيزك الفني..."
            className="w-full px-5 py-4 bg-white/50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white resize-none transition-all shadow-sm leading-relaxed"
          />
        </div>

        <button
          type="submit" disabled={saving}
          className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white py-4 rounded-2xl font-black text-lg transition-all shadow-lg shadow-primary-500/30 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {saving ? <Loader2 className="w-6 h-6 animate-spin" /> : saveSuccess ? <CheckCircle2 className="w-6 h-6" /> : <Save className="w-6 h-6" />}
          {saving ? 'جاري توثيق البيانات...' : saveSuccess ? 'تم الحفظ بنجاح ✓' : 'حفظ البيانات'}
        </button>
      </div>
    </form>
  );

  const renderSecurityTab = () => (
    <form onSubmit={handleChangePassword} className="bg-white/70 dark:bg-zinc-900/50 backdrop-blur-2xl border border-slate-200/50 dark:border-white/5 rounded-2xl p-8 shadow-xl shadow-slate-200/50 dark:shadow-black/50 space-y-8 animate-in fade-in duration-500">
      <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3 mb-2">
        <div className="p-3 bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 rounded-xl">
          <Lock className="w-6 h-6" />
        </div>
        كلمة السر والأمان
      </h2>

      <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400 p-4 rounded-xl flex items-center gap-3 mb-6">
        <CheckCircle2 className="w-6 h-6 shrink-0" />
        <div>
          <p className="font-bold">حالة الأمان ممتاز</p>
          <p className="text-md font-medium opacity-80">حسابك محمي وموثق بشكل آمن.</p>
        </div>
      </div>

      <p className="text-slate-500 dark:text-zinc-400 font-medium">قم بتغيير كلمة المرور الخاصة بك التحديث حماية أكاديميتك.</p>

      {pwError && (
        <div className="bg-rose-50/80 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 px-5 py-4 rounded-2xl text-md font-bold flex items-center gap-3 backdrop-blur-md animate-in fade-in">
          <AlertCircle className="w-5 h-5 shrink-0" /> {pwError}
        </div>
      )}
      {pwSuccess && (
        <div className="bg-emerald-50/80 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-5 py-4 rounded-2xl text-md font-bold flex items-center gap-3 backdrop-blur-md animate-in fade-in">
          <CheckCircle2 className="w-5 h-5 shrink-0" /> تم تغيير كلمة السر بنجاح للمنصة!
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">كلمة السر الحالية</label>
          <PasswordInput
            id="account-current-pw"
            value={pwForm.current}
            onChange={e => setPwForm({ ...pwForm, current: e.target.value })}
            showValidator={false}
            className="px-5 py-4 bg-white/50 dark:bg-black/20"
            placeholder="أدخل كلمة السر الحالية"
          />
        </div>
        <div>
          <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">كلمة السر الجديدة</label>
          <PasswordInput
            id="account-new-pw"
            value={pwForm.newPw}
            onChange={e => setPwForm({ ...pwForm, newPw: e.target.value })}
            onValidityChange={(valid) => setPwForm(prev => ({ ...prev, isNewValid: valid }))}
            className="px-5 py-4 bg-white/50 dark:bg-black/20"
            placeholder="أدخل كلمة سر قوية"
          />
        </div>
        <div>
          <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">تأكيد كلمة السر الجديدة</label>
          <PasswordInput
            id="account-confirm-pw"
            value={pwForm.confirm}
            onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })}
            showValidator={false}
            className="px-5 py-4 bg-white/50 dark:bg-black/20"
            placeholder="أعد إدخال كلمة السر"
          />
        </div>

        <button
          type="submit" disabled={pwSaving || (pwForm.newPw.length > 0 && !pwForm.isNewValid)}
          className="w-full flex items-center justify-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200 py-4 rounded-2xl font-black text-lg transition-all shadow-lg shadow-black/10 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {pwSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
          {pwSaving ? 'جاري التحقق...' : 'تحديث كلمة السر'}
        </button>
      </div>
    </form>
  );

  const renderAcademicRecordTab = () => {
    if (loadingAcademic) {
      return (
        <div className="flex justify-center items-center py-20 bg-white/70 dark:bg-zinc-900/50 backdrop-blur-2xl border border-slate-200/50 dark:border-white/5 rounded-2xl shadow-xl">
          <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
        </div>
      );
    }

    if (academicData.length === 0) {
      return (
        <div className="bg-white/70 dark:bg-zinc-900/50 backdrop-blur-2xl border border-slate-200/50 dark:border-white/5 rounded-2xl p-10 text-center shadow-xl animate-in fade-in duration-500">
          <div className="w-20 h-20 bg-slate-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-10 h-10 text-slate-400 dark:text-zinc-500" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">لا يوجد سجل تعليمي</h3>
          <p className="text-slate-500 dark:text-zinc-400">لم تقم بالانضمام لأي دورات حتى الآن.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="bg-gradient-to-l from-primary-800 to-primary-600 rounded-2xl p-8 text-white shadow-xl flex items-center justify-between overflow-hidden relative group">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-[100px] pointer-events-none group-hover:scale-110 transition-transform"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-black mb-2 shadow-sm drop-shadow-sm">بياناتي التعليمية</h2>
            <p className="text-primary-100 font-bold text-lg">سجل شامل لرحلتك الفنية وإنجازاتك في المنصة.</p>
          </div>
          <div className="relative z-10 w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center hidden sm:flex border border-white/20 shadow-inner">
            <GraduationCap className="w-10 h-10 text-white" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {academicData.map(({ course, enrollment }) => {
            const totalLessons = course.curriculum?.reduce((sum, unit) => sum + (unit.lessons?.length || 0), 0) || 1;
            const completedCount = enrollment.completedLessons?.length || 0;
            const remainingLessons = Math.max(0, totalLessons - completedCount);
            const points = enrollment.pointsEarned || 0;
            const totalPossible = totalLessons * 100;
            const percent = totalPossible > 0 ? Math.min(100, Math.round((points / totalPossible) * 100)) : 0;

            const isPassed = percent >= 80 || completedCount === totalLessons;

            return (
              <div key={course.id} className="bg-white/70 dark:bg-zinc-900/50 backdrop-blur-2xl border border-slate-200/50 dark:border-white/5 rounded-2xl p-6 shadow-xl shadow-slate-200/30 dark:shadow-black/40 flex flex-col md:flex-row gap-8 relative overflow-hidden group hover:shadow-2xl transition-all">

                {/* Visual Area */}
                <div className="w-full md:w-56 shrink-0 aspect-[16/10] md:aspect-square rounded-xl overflow-hidden relative border border-slate-200 dark:border-white/10">
                  <img src={course.thumbnail || ''} alt={course.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" />
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-all group-hover:backdrop-blur-0 duration-500"></div>

                  {/* Cinematic Progress Ring Overlay */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-white">
                    <div className="relative w-24 h-24 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-full border border-white/20 shadow-2xl mb-2" dir="ltr">
                      <svg className="absolute inset-0 w-full h-full -rotate-90 p-1" viewBox="0 0 52 52">
                        <circle className="stroke-white/10 fill-transparent" strokeWidth="4" cx="26" cy="26" r="22" />
                        <circle className={`fill-transparent transition-all duration-1000 ease-out ${isPassed ? 'stroke-emerald-400' : 'stroke-primary-500'}`} strokeWidth="4" strokeLinecap="round" strokeDasharray={138} strokeDashoffset={138 - (percent / 100) * 138} cx="26" cy="26" r="22" />
                      </svg>
                      <span className="text-2xl font-black text-white">{percent}%</span>
                    </div>
                  </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-2xl font-black text-slate-900 dark:text-white leading-tight">{course.title}</h3>
                      <div className={`px-4 py-1.5 rounded-xl text-md font-black shrink-0 border ${isPassed ? 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20' : 'bg-primary-50 text-primary-600 border-primary-200 dark:bg-primary-500/10 dark:text-primary-400 dark:border-primary-500/20'}`}>
                        {isPassed ? 'اجتازت بنجاح' : 'قيد التقدم'}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                      <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-100 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10 transition-colors">
                        <p className="text-md font-bold text-slate-500 dark:text-slate-400 mb-1">الدروس المنجزة</p>
                        <p className="text-2xl font-black text-slate-800 dark:text-white">{completedCount}</p>
                      </div>
                      <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-100 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10 transition-colors">
                        <p className="text-md font-bold text-slate-500 dark:text-slate-400 mb-1">الدروس المتبقية</p>
                        <p className="text-2xl font-black text-slate-800 dark:text-white">{remainingLessons}</p>
                      </div>
                      <div className="bg-primary-50 dark:bg-primary-500/10 p-4 rounded-xl border border-primary-100 dark:border-primary-500/20 hover:border-primary-300 dark:hover:border-primary-500/30 transition-colors">
                        <p className="text-md font-bold text-primary-600 dark:text-primary-400 mb-1">الرصيد المكتسب</p>
                        <p className="text-2xl font-black text-primary-700 dark:text-primary-300">{points}</p>
                      </div>
                      <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-100 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10 transition-colors">
                        <p className="text-md font-bold text-slate-500 dark:text-slate-400 mb-1">النقاط الكلية</p>
                        <p className="text-2xl font-black text-slate-800 dark:text-white">{totalPossible}</p>
                      </div>
                    </div>
                  </div>

                  {enrollment.quizScores && enrollment.quizScores.length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-2">
                      {enrollment.quizScores.map((score, i) => (
                        <span key={i} className="px-3 py-1 bg-slate-100 dark:bg-white/10 rounded-lg text-md font-bold text-slate-700 dark:text-zinc-300">
                          اختبار {i + 1}: {score.score}/{score.total}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 pt-5 border-t border-slate-100 dark:border-white/10">
                    <div className="flex items-center gap-3">
                      <span className="text-md font-bold text-slate-500 dark:text-zinc-400 whitespace-nowrap">شريط التقدم الكلي</span>
                      <div className="w-full h-2.5 bg-slate-100 dark:bg-white/10 rounded-full overflow-hidden shadow-inner">
                        <div className={`h-full transition-all duration-1000 ${isPassed ? 'bg-emerald-500' : 'bg-primary-500 shadow-[0_0_10px_rgba(230,0,35,0.8)]'}`} style={{ width: `${percent}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    );
  };

  const renderJourneyTab = () => {
    if (loadingJourney) {
      return <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-primary-500" /></div>;
    }

    const courseJourneyItems = academicData
      .filter(item => (item.enrollment?.pointsEarned || 0) >= 10)
      .map(item => {
        const totalLessons = item.course.curriculum?.reduce((sum, unit) => sum + (unit.lessons?.length || 0), 0) || 1;
        return {
          id: `course-${item.course.id}`,
          assignmentName: 'إنجاز الدروس',
          courseTitle: item.course.title,
          grade: item.enrollment.pointsEarned,
          maxScore: totalLessons * 100,
          earnedPixels: Math.floor((item.enrollment.pointsEarned || 0) / 10),
          gradedAt: item.enrollment.createdAt ? { toDate: () => {
            // Using lastAccessed or standard date fallback if firestore timestamp isn't right
            if (item.enrollment.createdAt?.toDate) return item.enrollment.createdAt.toDate();
            return new Date(item.enrollment.createdAt);
          }} : null,
          isCourseProgress: true
        };
      });

    const combinedJourney = [...journeyData, ...courseJourneyItems].sort((a, b) => {
      const timeA = a.gradedAt?.toDate ? a.gradedAt.toDate().getTime() : 0;
      const timeB = b.gradedAt?.toDate ? b.gradedAt.toDate().getTime() : 0;
      return timeB - timeA;
    });

    const historicalPixels = combinedJourney.reduce((acc, sub) => {
      return acc + (sub.earnedPixels !== undefined ? sub.earnedPixels : Math.floor((sub.grade || 0) / 10));
    }, 0);
    
    const currentPixels = Math.max((userData?.pixels || 0), historicalPixels);
    const pixelCollectorProgress = Math.min(100, Math.round((currentPixels / 100) * 100));

    const BADGES = [
      { id: 'perfect_pixel', title: 'البيكسل المثالي', desc: 'يُمنح عند حصولك على الدرجة النهائية كاملة (100٪) في التكليف المرفوع.', icon: Medal, color: 'text-amber-500', bg: 'bg-amber-500/20', border: 'border-amber-500/30' },
      { id: 'pixel_collector', title: 'جامع البكسلات', desc: 'يُمنح تلقائياً عند تجاوز رصيدك الكلي 100 بيكسل في المنصة.', icon: Award, color: 'text-emerald-500', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' },
      { id: 'golden_hand', title: 'اليد الذهبية', desc: 'شارة إدارية تُمنح للمشاريع ذات اللمسة التصميمية المتقنة والمميزة جداً.', icon: Star, color: 'text-yellow-400', bg: 'bg-yellow-400/20', border: 'border-yellow-400/30' },
      { id: 'designers_eye', title: 'عين المصمم', desc: 'شارة إدارية تُمنح للتطبيق المتميز لمبادئ التصميم.', icon: Eye, color: 'text-sky-500', bg: 'bg-sky-500/20', border: 'border-sky-500/30' },
      { id: 'inspirer', title: 'شارة المُلهِم', desc: 'شارة إدارية تُمنح للأفكار الإبداعية الخلّاقة التي تلهم الآخرين.', icon: Sparkles, color: 'text-indigo-500', bg: 'bg-indigo-500/20', border: 'border-indigo-500/30' }
    ];

    return (
      <div className="space-y-8 animate-in fade-in duration-500">
        
        {/* Header Summary */}
        <div className="bg-gradient-to-l from-slate-900 to-zinc-800 rounded-3xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[80px] pointer-events-none"></div>
          <div className="relative z-10 text-center md:text-right">
            <h2 className="text-3xl font-black mb-2 flex items-center gap-3 justify-center md:justify-start">
              <Compass className="w-8 h-8 text-amber-500" />
              حقيبة الترحال الخاصة بك
            </h2>
            <p className="text-slate-300 font-medium text-lg mt-2">رحلتك التعليمية مليئة بالإنجازات. تابعها خطوة بخطوة!</p>
          </div>
          <div className="relative z-10 flex flex-col items-center justify-center p-6 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 min-w-[200px]">
            <span className="text-slate-300 font-bold mb-2">رصيدك الكلي المكتسب</span>
            <div className="flex items-center gap-3">
              <span className="text-6xl font-black text-amber-400 font-mono tracking-tighter" dir="ltr">{currentPixels}</span>
              <div className="relative flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" className="w-12 h-12 drop-shadow-[0_0_15px_rgba(245,158,11,0.5)]">
                  <path d="M11.25 1.5C11.5833 0.833333 12.4167 0.833333 12.75 1.5L15.3582 6.7164C15.6568 7.31351 16.1865 7.84318 16.7836 8.14175L22.5 11.25C23.1667 11.5833 23.1667 12.4167 22.5 12.75L16.7836 15.8582C16.1865 16.1568 15.6568 16.6865 15.3582 17.2836L12.75 22.5C12.4167 23.1667 11.5833 23.1667 11.25 22.5L8.64175 17.2836C8.34318 16.6865 7.81351 16.1568 7.21639 15.8582L1.5 12.75C0.833333 12.4167 0.833333 11.5833 1.5 11.25L7.21639 8.14175C7.81351 7.84318 8.34318 7.31351 8.64175 6.7164L11.25 1.5Z" fill="url(#pixel-glow-hero)" />
                  <path d="M12 5L13.8 8.6C14.1 9.2 14.8 9.9 15.4 10.2L19 12L15.4 13.8C14.8 14.1 14.1 14.8 13.8 15.4L12 19L10.2 15.4C9.9 14.8 9.2 14.1 8.6 13.8L5 12L8.6 10.2C9.2 9.9 9.9 9.2 10.2 8.6L12 5Z" fill="white" opacity="0.7"/>
                  <defs>
                    <linearGradient id="pixel-glow-hero" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FCD34D" />
                      <stop offset="0.5" stopColor="#F59E0B" />
                      <stop offset="1" stopColor="#D97706" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* ═══ Badge Showcase — Premium Gamification Section ═══ */}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
          {/* Dramatic Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-zinc-900 to-slate-950"></div>
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary-500/8 rounded-full blur-[120px] pointer-events-none"></div>
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-amber-500/6 rounded-full blur-[100px] pointer-events-none"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>

          <div className="relative z-10 p-8 md:p-10">
            {/* Section Header */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-10">
              <div className="flex items-center gap-4 text-center sm:text-right">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30 shrink-0">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight">خزانة الشارات الاستثنائية</h3>
                  <p className="text-slate-400 font-medium text-sm mt-1">اجمعها كلها وأثبت جدارتك كمصمم استثنائي ✨</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white/5 backdrop-blur-md border border-white/10 px-5 py-2.5 rounded-2xl">
                <span className="text-sm font-bold text-slate-400">مكتسبة</span>
                <span className="text-xl font-black text-amber-400 font-mono" dir="ltr">{userData?.badges?.length || 0}/{BADGES.length}</span>
              </div>
            </div>
          
            {/* Badge Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {BADGES.map((badge, index) => {
                const Icon = badge.icon;
                const hasBadge = userData?.badges?.includes(badge.id);
                const rarityLabels = { perfect_pixel: 'نادرة', pixel_collector: 'تلقائية', golden_hand: 'أسطورية', designers_eye: 'نخبوية', inspirer: 'ملهمة' };
                const rarityColors = { perfect_pixel: 'text-amber-400 bg-amber-400/10 border-amber-400/20', pixel_collector: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', golden_hand: 'text-yellow-300 bg-yellow-300/10 border-yellow-300/20', designers_eye: 'text-sky-400 bg-sky-400/10 border-sky-400/20', inspirer: 'text-indigo-400 bg-indigo-400/10 border-indigo-400/20' };
                
                return (
                  <div 
                    key={badge.id} 
                    className={`group relative flex flex-col rounded-2xl border transition-all duration-700 cursor-default ${hasBadge ? 'badge-card-earned badge-glow bg-gradient-to-br from-white/[0.08] to-white/[0.02] border-white/15 hover:border-white/30 hover:scale-[1.02]' : 'badge-locked-shimmer bg-white/[0.03] border-white/[0.06] hover:border-white/10 hover:bg-white/[0.05]'}`}
                    style={{ '--badge-index': index }}
                  >
                    {/* Earned Glow Ring */}
                    {hasBadge && (
                      <div className="absolute -inset-[1px] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{background: `linear-gradient(135deg, transparent, ${badge.color.includes('amber') ? 'rgba(245,158,11,0.15)' : badge.color.includes('emerald') ? 'rgba(16,185,129,0.15)' : badge.color.includes('yellow') ? 'rgba(250,204,21,0.15)' : badge.color.includes('sky') ? 'rgba(14,165,233,0.15)' : 'rgba(99,102,241,0.15)'}, transparent)`}}></div>
                    )}

                    <div className="relative z-10 p-6">
                      {/* Rarity Label */}
                      <div className="flex items-center justify-between mb-5">
                        <span className={`text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded-full border ${rarityColors[badge.id]}`}>
                          {rarityLabels[badge.id]}
                        </span>
                        {hasBadge ? (
                          <span className="flex items-center gap-1.5 text-xs font-black text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full border border-emerald-400/20">
                            <CheckCircle2 className="w-3.5 h-3.5"/>مكتسبة
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-xs font-bold text-slate-500 bg-white/5 px-3 py-1 rounded-full border border-white/10">
                            <Lock className="w-3 h-3"/>مقفلة
                          </span>
                        )}
                      </div>

                      {/* Icon with Decorative Ring */}
                      <div className="flex justify-center mb-5">
                        <div className="relative">
                          {/* Rotating Ring */}
                          {hasBadge && (
                            <div className="absolute -inset-2 badge-ring opacity-40">
                              <svg className="w-full h-full" viewBox="0 0 80 80">
                                <circle cx="40" cy="40" r="36" fill="none" stroke={`url(#ringGrad-${badge.id})`} strokeWidth="1" strokeDasharray="6 4" opacity="0.6"/>
                                <defs>
                                  <linearGradient id={`ringGrad-${badge.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor={badge.color.includes('amber') ? '#f59e0b' : badge.color.includes('emerald') ? '#10b981' : badge.color.includes('yellow') ? '#facc15' : badge.color.includes('sky') ? '#0ea5e9' : '#6366f1'} />
                                    <stop offset="100%" stopColor="transparent" />
                                  </linearGradient>
                                </defs>
                              </svg>
                            </div>
                          )}
                          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-500 ${hasBadge ? `${badge.bg} border ${badge.border} shadow-lg` : 'bg-white/[0.06] border border-white/10'}`}>
                            <Icon className={`w-8 h-8 transition-all duration-500 ${hasBadge ? `${badge.color} badge-icon-animate` : 'text-slate-600'}`} style={hasBadge ? { '--badge-index': index } : {}} />
                          </div>
                        </div>
                      </div>

                      {/* Badge Title & Description */}
                      <div className="text-center">
                        <h4 className={`font-black text-lg mb-2 transition-colors ${hasBadge ? 'text-white' : 'text-slate-400'}`}>{badge.title}</h4>
                        <p className={`text-sm leading-relaxed ${hasBadge ? 'text-slate-400' : 'text-slate-600'}`}>{badge.desc}</p>
                      </div>

                      {/* Pixel Collector Progress Bar */}
                      {badge.id === 'pixel_collector' && (
                        <div className="mt-5 pt-5 border-t border-white/[0.06]">
                          <div className="flex justify-between items-center mb-2.5">
                            <span className="text-xs font-bold text-slate-500">التقدم نحو 100 بيكسل</span>
                            <span className={`text-sm font-black font-mono ${pixelCollectorProgress >= 100 ? 'text-emerald-400' : 'text-amber-400'}`} dir="ltr">{pixelCollectorProgress}%</span>
                          </div>
                          <div className="w-full h-2.5 bg-white/[0.06] rounded-full overflow-hidden relative">
                            <div 
                              className={`h-full rounded-full transition-all duration-1000 ease-out ${pixelCollectorProgress >= 100 ? 'bg-gradient-to-l from-emerald-400 to-emerald-500 progress-bar-glow' : 'bg-gradient-to-l from-amber-400 to-orange-500'}`}
                              style={{width: `${pixelCollectorProgress}%`}}
                            ></div>
                            {/* Active shimmer on progress bar */}
                            {pixelCollectorProgress < 100 && pixelCollectorProgress > 0 && (
                              <div className="absolute inset-0 overflow-hidden rounded-full pointer-events-none">
                                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-white/20 to-transparent" style={{width: '30%', marginRight: `${pixelCollectorProgress - 15}%`, animation: 'shimmer-sweep 2s ease-in-out infinite'}}></div>
                              </div>
                            )}
                          </div>
                          {pixelCollectorProgress < 100 && (
                            <p className="text-[11px] text-slate-600 mt-2 text-center font-medium">باقي <span className="text-amber-400 font-black">{100 - currentPixels}</span> بيكسل لفتح هذه الشارة!</p>
                          )}
                        </div>
                      )}

                      {/* Locked Challenge CTA */}
                      {!hasBadge && badge.id !== 'pixel_collector' && (
                        <div className="mt-5 pt-4 border-t border-white/[0.06] text-center">
                          <p className="text-[11px] text-slate-600 font-medium">⚡ واصل تعلّمك لفتح هذا الإنجاز</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Pixel History Ledger */}
        <div className="bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl border border-slate-200/50 dark:border-white/5 rounded-3xl p-8 shadow-xl">
           <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-6 flex items-center gap-3">
             <History className="w-6 h-6 text-primary-500" /> كشف حساب البيكسلات
           </h3>
           
           {combinedJourney.length === 0 ? (
             <div className="text-center py-12">
               <div className="w-16 h-16 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                 <Calendar className="w-8 h-8 text-slate-400" />
               </div>
               <p className="text-slate-500 dark:text-zinc-400 font-bold">لم تقم بإنجاز أي تكاليف أو دروس حتى الآن.</p>
               <p className="text-sm text-slate-400 mt-1">شاهد الدروس لتحصد أول بيكسلاتك!</p>
             </div>
           ) : (
             <div className="space-y-4">
               {combinedJourney.map((sub, i) => (
                 <div key={sub.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl hover:border-primary-500/30 transition-all gap-4">
                   <div className="flex items-center gap-4">
                     <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 border font-black font-mono ${sub.isCourseProgress ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 border-emerald-200 dark:border-emerald-500/30' : 'bg-primary-100 dark:bg-primary-500/20 text-primary-600 border-primary-200 dark:border-primary-500/30'}`}>
                       {sub.isCourseProgress ? <Play className="w-5 h-5 fill-current" /> : i + 1}
                     </div>
                     <div>
                       <h4 className="font-bold text-slate-900 dark:text-white text-lg">{sub.assignmentName}</h4>
                       <p className="text-sm font-medium text-slate-500 dark:text-zinc-400 mt-1">
                         دورة: {sub.courseTitle || 'غير محدد'} {sub.gradedAt?.toDate && `• بتاريخ: ${sub.gradedAt.toDate().toLocaleDateString('en-GB')}`}
                       </p>
                     </div>
                   </div>
                   <div className="flex items-center gap-6 shrink-0 bg-white dark:bg-black/30 px-5 py-3 rounded-xl border border-slate-100 dark:border-white/5">
                     <div className="text-center border-l-2 border-slate-100 dark:border-white/10 pl-6">
                       <span className="block text-[10px] sm:text-xs font-bold text-slate-400 mb-1">{sub.isCourseProgress ? 'مجموع الدرجات' : 'الدرجة'}</span>
                       <span className="font-black text-slate-700 dark:text-zinc-300 font-mono" dir="ltr">{sub.grade || 0}/{sub.maxScore || 100}</span>
                     </div>
                     <div className="text-center">
                       <span className="block text-[10px] sm:text-xs font-bold text-slate-400 mb-1">البيكسلات</span>
                       <span className="font-black text-amber-500 font-mono flex items-center justify-center gap-1" dir="ltr">
                         +{sub.earnedPixels !== undefined ? sub.earnedPixels : Math.floor((sub.grade || 0) / 10)}
                         <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 mr-0.5 mt-0.5">
                           <path d="M11.25 1.5C11.5833 0.833333 12.4167 0.833333 12.75 1.5L15.3582 6.7164C15.6568 7.31351 16.1865 7.84318 16.7836 8.14175L22.5 11.25C23.1667 11.5833 23.1667 12.4167 22.5 12.75L16.7836 15.8582C16.1865 16.1568 15.6568 16.6865 15.3582 17.2836L12.75 22.5C12.4167 23.1667 11.5833 23.1667 11.25 22.5L8.64175 17.2836C8.34318 16.6865 7.81351 16.1568 7.21639 15.8582L1.5 12.75C0.833333 12.4167 0.833333 11.5833 1.5 11.25L7.21639 8.14175C7.81351 7.84318 8.34318 7.31351 8.64175 6.7164L11.25 1.5Z" fill="url(#pixel-glow-small)" />
                           <path d="M12 5L13.8 8.6C14.1 9.2 14.8 9.9 15.4 10.2L19 12L15.4 13.8C14.8 14.1 14.1 14.8 13.8 15.4L12 19L10.2 15.4C9.9 14.8 9.2 14.1 8.6 13.8L5 12L8.6 10.2C9.2 9.9 9.9 9.2 10.2 8.6L12 5Z" fill="white" opacity="0.7"/>
                           <defs>
                             <linearGradient id="pixel-glow-small" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                               <stop stopColor="#FCD34D" />
                               <stop offset="0.5" stopColor="#F59E0B" />
                               <stop offset="1" stopColor="#D97706" />
                             </linearGradient>
                           </defs>
                         </svg>
                       </span>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
           )}
        </div>

      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 relative animate-in fade-in duration-700">

      {/* Ambient Glows */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-500/20 dark:bg-primary-600/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none transition-transform duration-700"></div>

      <div className="relative z-10 mb-8">
        <h1 className="text-4xl font-black text-slate-900 dark:text-white mb-3 flex items-center gap-4 drop-shadow-sm">
          لوحة إعدادات الاستوديو
        </h1>
        <p className="text-lg font-medium text-slate-500 dark:text-zinc-400">قم بضبط هويتك وبياناتك الشخصية للمنصة ومتابعة أدائك الأكاديمي.</p>
      </div>

      {/* Profile incomplete alert */}
      {!isProfileComplete && (
        <div className="relative z-10 bg-gradient-to-r from-rose-50 to-rose-100 dark:from-rose-950/40 dark:to-zinc-900 border border-rose-200 dark:border-rose-500/20 rounded-2xl p-6 md:p-8 flex items-start gap-5 shadow-lg shadow-rose-100/50 dark:shadow-rose-900/20 mb-8">
          <div className="w-14 h-14 bg-rose-100 dark:bg-rose-500/20 rounded-2xl flex items-center justify-center shrink-0 border border-rose-200 dark:border-rose-500/30">
            <AlertCircle className="w-7 h-7 text-rose-600 dark:text-rose-400" />
          </div>
          <div>
            <p className="font-black text-rose-800 dark:text-rose-300 text-xl mb-1">بياناتك غير مكتملة</p>
            <p className="text-rose-600 dark:text-rose-400/80 font-bold">يجب تعبئة الحقول المطلوبة في البيانات الشخصية حتى تتمكن من التسجيل في الدورات والوصول الكامل.</p>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-8 relative z-10">

        {/* Right Sidebar Navigation (RTL) */}
        <div className="lg:w-80 shrink-0">
          <div className="bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl border border-slate-200/50 dark:border-white/10 rounded-3xl p-4 shadow-xl shadow-slate-200/50 dark:shadow-black/50 sticky top-24 space-y-2">
            {[
              { id: 'personal', icon: User, label: 'إدارة البيانات الشخصية' },
              { id: 'security', icon: Lock, label: 'كلمة السر والأمان' },
              { id: 'academic', icon: GraduationCap, label: 'بياناتي التعليمية' },
              { id: 'journey', icon: Compass, label: 'الرحلة التعليمية' }
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-bold text-lg transition-all duration-300 outline-none ${isActive
                    ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 shadow-sm border border-primary-100 dark:border-primary-500/20'
                    : 'bg-transparent text-slate-600 dark:text-zinc-400 hover:bg-slate-50 hover:text-slate-900 dark:hover:bg-white/5 dark:hover:text-white border border-transparent'
                    }`}
                >
                  <Icon className={`w-6 h-6 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dynamic Content Area */}
        <div className="flex-1 min-w-0">
          {activeTab === 'personal' && renderPersonalDataTab()}
          {activeTab === 'security' && renderSecurityTab()}
          {activeTab === 'academic' && renderAcademicRecordTab()}
          {activeTab === 'journey' && renderJourneyTab()}
        </div>

      </div>
    </div>
  );
}
